import { useAuthStore } from './store/authStore'
import { capturePendingSegment } from './features/product-segment'
import { captureOnboardingVariant, captureLandingQuiz, clearLandingQuizCookie } from './features/onboarding'
import { getProductConfig } from './app/product-config/registry'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App'
import { initMetrica } from './lib/metrica'
import { initializeAppAnalytics, openAppVisit } from './app/analytics'
import { useInstallStore, type BeforeInstallPromptEvent } from './store/installStore'
import { App as CapacitorApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { isNative, OAUTH_NATIVE_RETURN } from './lib/platform'
import { applyOAuthParams } from './lib/oauthReturn'
import './styles/global.css'

// Restore cached Metrica UID before anything renders (identifies returning users).
initMetrica()
// Capture advertising attribution before auth/invite helpers remove query parameters.
initializeAppAnalytics()

// Web: capture from the URL bar on load.
if (!useAuthStore.getState().isAuthenticated()) {
  const url = new URL(window.location.href)
  const segment = capturePendingSegment(url)
  if (segment === 'break_up' && captureLandingQuiz(getProductConfig(segment).onboarding)) {
    url.searchParams.set('variant', 'default')
    window.history.replaceState(window.history.state, '', url)
  }
  captureOnboardingVariant(url, segment)
} else clearLandingQuizCookie()
applyOAuthParams(new URLSearchParams(window.location.search), { stripUrl: true })
openAppVisit()

// Native (Capacitor, Phase 2.5): the backend 302s to com.humanteq.app://oauth/yandex
// ?auth_token=... after exchanging the code. Android opens the app via the manifest
// scheme intent-filter; CapacitorApp.appUrlOpen hands the query to the same path.
if (isNative) {
  CapacitorApp.addListener('appUrlOpen', ({ url }) => {
    if (url && url.startsWith(OAUTH_NATIVE_RETURN)) {
      const i = url.indexOf('?')
      // Сессия попадёт в стор, а с экрана входа уведёт GuestOnly — маршрут сам
      // по себе от прихода ссылки не меняется.
      applyOAuthParams(new URLSearchParams(i >= 0 ? url.slice(i + 1) : ''), { stripUrl: false })
      Browser.close().catch(() => { /* close the system-browser tab Yandex opened in */ })
    }
  })
}

// Capture the PWA install prompt as early as possible — the browser can fire
// `beforeinstallprompt` before React mounts. The InstallPrompt banner triggers it.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  useInstallStore.getState().setPrompt(e as BeforeInstallPromptEvent)
})
window.addEventListener('appinstalled', () => useInstallStore.getState().clear())

// One shared query client. Server state (history, profile, report status) lives
// here instead of in module instance fields like the old code did.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
)
